<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import WhiteListOfacRepository from '../../repositories/whiteListOfac.repository ';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { IWhiteListOfacRecord } from '../../types/whiteListOfac.type';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n';
import { validationRFC } from '../../helpers/validationRFC';
import DataTable from '@components/DataTable.vue';

const isLoadingWhiteListOfac = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { whiteListOfac: whiteListOfacPinia }: any = storeToRefs(store);
const whiteListOfac = ref<IWhiteListOfacRecord[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const showError = ref<string | null>(null);
const selectedWhiteListOfacRecord = ref<IWhiteListOfacRecord | null>(null);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'tributaryCode',
    label: 'Codigo tributario',
  },
  {
    key: 'observation',
    label: 'Observaciones',
  },
  {
    key: 'allowClient',
    label: '¿Omite control OFAC?',
    formatter: (e: any) => (e ? 'SI' : 'NO'),
  },
  {
    key: 'edit',
    label: t('global.editar'),
  },
]);

const getWhiteListOfacFromAPI = async (
  page: number,
  deleted: boolean = false,
) => {
  isLoadingWhiteListOfac.value = true;
  try {
    const { data } = await WhiteListOfacRepository.getWhiteListOfac({
      page,
      deleted,
    });
    if (data && Array.isArray(data?.whiteList)) {
      paginationData.value = data.pagination;
      return data.whiteList;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching whiteListOfac:', error);
    return [];
  } finally {
    isLoadingWhiteListOfac.value = false;
  }
};

const updateWhiteListOfac = (newWhiteListOfac: IWhiteListOfacRecord[]) => {
  whiteListOfac.value = newWhiteListOfac;
  store.setProp({ key: 'whiteListOfac', value: newWhiteListOfac });
};

const loadWhiteListOfac = async () => {
  const newWhiteListOfac = await getWhiteListOfacFromAPI(1);
  updateWhiteListOfac(newWhiteListOfac);
};

onMounted(loadWhiteListOfac);

// Create inputs from modals
const fieldsInputWhiteListOfacRecord = computed(() => {
  return [
    {
      key: 'tributaryCode',
      title: 'Codigo tributario',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t('global.es_obligatorio'),
        },
      },
      placeholder: t('global.nombre'),
      value: selectedWhiteListOfacRecord.value?.tributaryCode,
      cols: 12,
      errors: [],
    },
    {
      key: 'observation',
      title: 'Observación',
      type: 'text',
      placeholder: 'Observación',
      value: selectedWhiteListOfacRecord.value?.observation,
      cols: 12,
    },
    {
      key: 'allowClient',
      title: '¿Omite control de OFAC?',
      type: 'select',
      selectLabel: null,
      options: [
        { id: 1, nombre: 'SI' },
        { id: 2, nombre: 'NO' },
      ],
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t('global.es_obligatorio'),
        },
      },
      placeholder: '¿Omite control de OFAC?',
      value: selectedWhiteListOfacRecord.value?.allowClient ? 1 : 2,
      cols: 12,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getWhiteListOfacFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = 'Crear Excepción';
  isModalOpen.value = true;
  modalType.value = 'modalCreateException';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedWhiteListOfacRecord.value = null;
};

//CRUD
const handleCreateWhiteListOfacRecord = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;

  const rfcIsValid = validationRFC(items.tributaryCode);

  if (!rfcIsValid) {
    showError.value = 'El RFC ingresado no es valido';
    setTimeout(() => {
      showError.value = null;
    }, 2000);
    return;
  }

  const whiteListOfacRecordForCreate = {
    ...items,
    allowClient: items.allowClient.id === 1 ? true : false,
  };

  try {
    const data = await WhiteListOfacRepository.createWhiteListOfacRecord(
      whiteListOfacRecordForCreate,
    );
    store.setProp({
      key: 'whiteListOfac',
      value: [...whiteListOfac.value, data],
    });
    $toast?.success('Excepción creada con exito');
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleSelectWhiteListOfacRecord = (
  whiteListOfacRecordData: IWhiteListOfacRecord,
) => {
  modalTitle.value = 'Actualizar excepción';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateWhiteListOfac');
  selectedWhiteListOfacRecord.value = whiteListOfacRecordData;
};

const handleUpdateWhiteListOfacRecord = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedWhiteListOfacRecord.value) return;

  const rfcIsValid = validationRFC(items.tributaryCode);

  if (!rfcIsValid) {
    showError.value = 'El RFC ingresado no es valido';
    setTimeout(() => {
      showError.value = null;
    }, 2000);
    return;
  }

  const whiteListOfacRecordForUpdate = {
    ...items,
    id: selectedWhiteListOfacRecord.value.id,
    allowClient: items.allowClient.id === 1 ? true : false,
  };

  try {
    const data = await WhiteListOfacRepository.updateWhiteListOfacRecord(
      whiteListOfacRecordForUpdate,
    );

    let index = store.whiteListOfac.findIndex(
      (value: any) => value.id === data.id,
    );

    if (index > -1) {
      store.setProp({ key: 'whiteListOfac', index, value: data });
    }
    $toast?.success('Excepción actualizada con exito');
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};
</script>

<template>
  <Layout
    :title="t('ajustes.excepciones_ofac.excepciones_ofac')"
    :textColor="'color-secondary'"
  >
    <template #layout>
      <div class="d-flex justify-content-end">
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>Agregar excepción</template>
        </CButton>
      </div>

      <DataTable
        :loading="isLoadingWhiteListOfac"
        :fields="fieldsTableActive"
        :items="whiteListOfacPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(edit)="whiteListOfacRecordData">
          <div
            @click="
              handleSelectWhiteListOfacRecord(whiteListOfacRecordData.data.item)
            "
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateException'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputWhiteListOfacRecord"
            @onSubmit="handleCreateWhiteListOfacRecord"
          >
            <template #buttons>
              <p v-if="showError" class="text-danger mt-3">{{ showError }}</p>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Agregar excepción</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateWhiteListOfac'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputWhiteListOfacRecord"
            @onSubmit="handleUpdateWhiteListOfacRecord"
          >
            <template #buttons>
              <p v-if="showError" class="text-danger mt-3">{{ showError }}</p>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
